import { Card, CardPerformance } from '../types';

export interface SpacedRepetitionCard extends Card {
  easeFactor: number;
  interval: number;
  repetitions: number;
  nextReviewDate: Date;
  lastReviewed?: Date;
}

export class SpacedRepetitionService {
  private readonly MIN_EASE_FACTOR = 1.3;
  private readonly DEFAULT_EASE_FACTOR = 2.5;
  private readonly EASE_FACTOR_MODIFIER = 0.1;
  private readonly MIN_INTERVAL = 1;

  /**
   * Calculate the next review parameters based on the SM-2 algorithm
   * @param card Current card state
   * @param grade Performance grade (0=incorrect, 1=correct but difficult, 2=correct)
   * @returns Updated card parameters
   */
  public calculateNextReview(
    card: SpacedRepetitionCard, 
    grade: number
  ): Partial<SpacedRepetitionCard> {
    let { easeFactor, interval, repetitions } = card;
    
    // Ensure grade is within bounds
    grade = Math.max(0, Math.min(2, grade));
    
    if (grade >= 1) {
      // Correct answer
      if (repetitions === 0) {
        interval = 1;
      } else if (repetitions === 1) {
        interval = 6;
      } else {
        interval = Math.round(interval * easeFactor);
      }
      repetitions += 1;
    } else {
      // Incorrect answer - reset the learning process
      repetitions = 0;
      interval = 1;
    }

    // Update ease factor based on performance
    const newEaseFactor = easeFactor + (0.1 - (2 - grade) * (0.08 + (2 - grade) * 0.02));
    easeFactor = Math.max(this.MIN_EASE_FACTOR, newEaseFactor);

    // Calculate next review date
    const nextReviewDate = new Date();
    nextReviewDate.setDate(nextReviewDate.getDate() + interval);

    return {
      easeFactor,
      interval,
      repetitions,
      nextReviewDate,
      lastReviewed: new Date()
    };
  }

  /**
   * Get cards that are due for review
   * @param cards Array of spaced repetition cards
   * @returns Cards that should be reviewed now
   */
  public getDueCards(cards: SpacedRepetitionCard[]): SpacedRepetitionCard[] {
    const now = new Date();
    return cards.filter(card => 
      !card.nextReviewDate || card.nextReviewDate <= now
    ).sort((a, b) => {
      // Sort by priority: overdue cards first, then by next review date
      const aOverdue = a.nextReviewDate ? now.getTime() - a.nextReviewDate.getTime() : 0;
      const bOverdue = b.nextReviewDate ? now.getTime() - b.nextReviewDate.getTime() : 0;
      return bOverdue - aOverdue;
    });
  }

  /**
   * Convert regular cards to spaced repetition cards with performance data
   * @param cards Regular cards
   * @param performances Performance history
   * @returns Spaced repetition cards with calculated parameters
   */
  public convertToSpacedRepetitionCards(
    cards: Card[], 
    performances: Map<string, CardPerformance[]>
  ): SpacedRepetitionCard[] {
    return cards.map(card => {
      const cardPerformances = performances.get(card.id) || [];
      const srCard = this.initializeSpacedRepetitionCard(card, cardPerformances);
      return srCard;
    });
  }

  /**
   * Initialize a spaced repetition card based on performance history
   * @param card Original card
   * @param performances Performance history for this card
   * @returns Initialized spaced repetition card
   */
  private initializeSpacedRepetitionCard(
    card: Card, 
    performances: CardPerformance[]
  ): SpacedRepetitionCard {
    if (performances.length === 0) {
      // New card - needs initial review
      return {
        ...card,
        easeFactor: this.DEFAULT_EASE_FACTOR,
        interval: this.MIN_INTERVAL,
        repetitions: 0,
        nextReviewDate: new Date() // Due now
      };
    }

    // Calculate current state based on performance history
    let easeFactor = this.DEFAULT_EASE_FACTOR;
    let interval = this.MIN_INTERVAL;
    let repetitions = 0;
    let lastReviewed: Date | undefined;

    // Sort performances by timestamp
    const sortedPerformances = performances.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Apply each performance to calculate current state
    for (const performance of sortedPerformances) {
      const grade = performance.isCorrect ? 2 : 0; // Simple mapping for now
      
      if (grade >= 1) {
        if (repetitions === 0) {
          interval = 1;
        } else if (repetitions === 1) {
          interval = 6;
        } else {
          interval = Math.round(interval * easeFactor);
        }
        repetitions += 1;
      } else {
        repetitions = 0;
        interval = 1;
      }

      const newEaseFactor = easeFactor + (0.1 - (2 - grade) * (0.08 + (2 - grade) * 0.02));
      easeFactor = Math.max(this.MIN_EASE_FACTOR, newEaseFactor);
      lastReviewed = new Date(performance.timestamp);
    }

    // Calculate next review date based on last review and current interval
    const nextReviewDate = new Date(lastReviewed || new Date());
    nextReviewDate.setDate(nextReviewDate.getDate() + interval);

    return {
      ...card,
      easeFactor,
      interval,
      repetitions,
      nextReviewDate,
      lastReviewed
    };
  }

  /**
   * Get optimal batch size for study session
   * @param totalCards Total number of cards available
   * @param sessionDurationMinutes Target session duration in minutes
   * @returns Recommended number of cards for the session
   */
  public getOptimalBatchSize(
    totalCards: number, 
    sessionDurationMinutes: number = 20
  ): number {
    // Assume ~2 minutes per card on average
    const cardsPerMinute = 0.5;
    const optimalCards = Math.floor(sessionDurationMinutes * cardsPerMinute);
    return Math.min(optimalCards, totalCards);
  }

  /**
   * Calculate study statistics
   * @param performances All performance records
   * @returns Study statistics
   */
  public calculateStudyStats(performances: CardPerformance[]) {
    if (performances.length === 0) {
      return {
        totalCards: 0,
        totalSessions: 0,
        averageAccuracy: 0,
        streakCurrent: 0,
        streakBest: 0,
        cardsLearned: 0,
        cardsMastered: 0
      };
    }

    const totalAttempts = performances.length;
    const correctAttempts = performances.filter(p => p.isCorrect).length;
    const averageAccuracy = Math.round((correctAttempts / totalAttempts) * 100);

    // Calculate streaks
    const sortedPerformances = performances.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    let currentStreak = 0;
    let bestStreak = 0;
    let tempStreak = 0;

    for (let i = sortedPerformances.length - 1; i >= 0; i--) {
      if (sortedPerformances[i].isCorrect) {
        tempStreak++;
        if (i === sortedPerformances.length - 1) {
          currentStreak = tempStreak;
        }
      } else {
        if (i === sortedPerformances.length - 1) {
          currentStreak = 0;
        }
        bestStreak = Math.max(bestStreak, tempStreak);
        tempStreak = 0;
      }
    }
    bestStreak = Math.max(bestStreak, tempStreak);

    // Count unique cards and sessions
    const uniqueCards = new Set(performances.map(p => p.cardId)).size;
    const uniqueSessions = new Set(performances.map(p => p.sessionId)).size;

    // Count mastered cards (cards with repetitions >= 3 and ease factor > 2.5)
    const cardStats = new Map<string, { correct: number, total: number }>();
    performances.forEach(p => {
      const stats = cardStats.get(p.cardId) || { correct: 0, total: 0 };
      stats.total++;
      if (p.isCorrect) stats.correct++;
      cardStats.set(p.cardId, stats);
    });

    let cardsLearned = 0;
    let cardsMastered = 0;
    cardStats.forEach(stats => {
      if (stats.total >= 2 && stats.correct / stats.total >= 0.7) {
        cardsLearned++;
      }
      if (stats.total >= 5 && stats.correct / stats.total >= 0.8) {
        cardsMastered++;
      }
    });

    return {
      totalCards: uniqueCards,
      totalSessions: uniqueSessions,
      averageAccuracy,
      streakCurrent: currentStreak,
      streakBest: bestStreak,
      cardsLearned,
      cardsMastered
    };
  }

  /**
   * Get learning recommendations based on performance
   * @param cards Spaced repetition cards
   * @returns Learning recommendations
   */
  public getLearningRecommendations(cards: SpacedRepetitionCard[]) {
    const dueCards = this.getDueCards(cards);
    const overdueCards = cards.filter(card => {
      if (!card.nextReviewDate) return false;
      return new Date() > card.nextReviewDate;
    });

    const difficultCards = cards
      .filter(card => card.easeFactor < 2.0)
      .sort((a, b) => a.easeFactor - b.easeFactor);

    const newCards = cards.filter(card => card.repetitions === 0);

    return {
      dueCards: dueCards.slice(0, 10), // Limit to 10 most important
      overdueCards: overdueCards.slice(0, 5),
      difficultCards: difficultCards.slice(0, 5),
      newCards: newCards.slice(0, 3),
      recommendedSessionSize: this.getOptimalBatchSize(dueCards.length),
      shouldReviewDifficult: difficultCards.length > 0,
      shouldLearnNew: newCards.length > 0 && dueCards.length < 5
    };
  }
}